<script setup lang="ts">
import KitchenPositionsWithUsers from '@/components/Manager/KitchenPositionsWithUsers.vue';
import SlotDetailsDialog from '@/components/Manager/SlotDetailsDialog.vue';
import CalendarGrid from '@/components/Planning/CalendarGrid.vue';
import DuplicateWeekShiftDialog from '@/components/Planning/DuplicateWeekShiftDialog.vue';
import { useEmployee } from '@/composables/useEmployee';
import { useFetch } from '@/composables/useFetch';
import { usePermissions } from '@/composables/usePermissions';
import { useEnvStore } from '@/stores/envStore';
import { useEmployeeStore } from '@/stores/useEmployeeStore';
import { useUserStore } from '@/stores/useUserStore';
// import type { Employee } from '@/types/api';
import type { ShiftAssignment, ShiftByDayAndSlot, ShiftSlot } from '@/types/employee';
import { getWeekDays } from '@/utils/date';
import Button from 'primevue/button';
import TabPanel from 'primevue/tabpanel';
import TabView from 'primevue/tabview';
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps<{
  weekRange: { start: Date; end: Date };
}>();

const { can } = usePermissions();
const { fetchData } = useFetch();
const { getAbsencesForWeek } = useEmployee();
const { t } = useI18n();
const userStore = useUserStore();
const apiUrl = useEnvStore().apiUrl;
const currentRestaurantId: string = userStore.user?.restaurants[0]?.id || '';
const employeeStore = useEmployeeStore();
const selectedSlots = ref<Array<{ day: string; slot: ShiftSlot }>>([]);

const isLoading = ref(false);
const weekDays = ref<Date[]>([]);

// Absences state
const absences = ref<Array<{ employee_name: string; date_ranges: string[]; total_days: number }>>([]);
const isLoadingAbsences = ref(false);

// Change from currentCategory to currentDepartment
const currentDepartment = ref<string | null>(null);

// Track active tab index to determine current department
const activeTabIndex = ref(0);

// Update current department when tab changes
watch(activeTabIndex, (newIndex) => {
  const departments = employeeStore.positionCategories; // This now contains department names
  currentDepartment.value = departments[newIndex] || null;
});

// Filter shift slots by department
const filteredShiftSlots = computed(() => {
  if (!currentDepartment.value) {
    console.log('No current department set, returning all shift slots');
    return employeeStore.shiftSlots.filter((slot) => !slot.is_default);
  }
  console.log(`Filtering shift slots for department: ${currentDepartment.value}`);

  console.log(`Available shift slots: ${employeeStore.shiftSlots.length}`);
  console.log('shift slots : ', employeeStore.shiftSlots);

  return employeeStore.shiftSlots.filter((slot) => !slot.is_default && slot.department?.name === currentDepartment.value);
});

// Initialize current department
watch(
  () => employeeStore.positionCategories,
  (departments) => {
    if (departments.length > 0 && !currentDepartment.value) {
      currentDepartment.value = departments[0];
    }
  },
  { immediate: true },
);

async function loadData(forceRefetch = false): Promise<void> {
  if (!currentRestaurantId) return;
  isLoading.value = true;

  const from = props.weekRange.start.toISOString().slice(0, 10);
  const to = props.weekRange.end.toISOString().slice(0, 10);

  await employeeStore.fetchShiftsIfNeeded({
    restaurantId: currentRestaurantId,
    startDate: from,
    endDate: to,
    forceRefetch,
  });
  await employeeStore.fetchRestaurantUsersIfNeeded();
  await employeeStore.fetchKitchenPositionsIfNeeded();
  await employeeStore.fetchShiftSlotsIfNeeded();

  isLoading.value = false;
}

const calendarGridData = computed<ShiftByDayAndSlot>(() => {
  const raw = employeeStore.shiftsByDayAndSlot as Record<string, Record<string, ShiftAssignment[]>>;
  const result: ShiftByDayAndSlot = {};
  for (const day in raw) {
    result[day] = {};
    for (const slot in raw[day]) {
      result[day][slot] = raw[day][slot].map((shift) => ({
        ...shift,
        restaurant_user_id: shift.restaurant_user_id ?? shift.user?.restaurant_user_id,
      }));
    }
  }
  return result;
});

// Filter calendar data by department
function getCalendarDataForDepartment(department: string): ShiftByDayAndSlot {
  const raw = employeeStore.shiftsByDayAndSlot as Record<string, Record<string, ShiftAssignment[]>>;
  const result: ShiftByDayAndSlot = {};

  for (const day in raw) {
    result[day] = {};
    for (const slot in raw[day]) {
      result[day][slot] = raw[day][slot]
        .filter((shift) => shift.kitchen_position?.department?.name === department)
        .map((shift) => ({
          ...shift,
          restaurant_user_id: shift.restaurant_user_id ?? shift.user?.restaurant_user_id,
        }));
    }
  }
  return result;
}

// Function to fetch absences for the current week
async function fetchAbsences() {
  if (!currentRestaurantId) return;

  isLoadingAbsences.value = true;
  try {
    const startDate = props.weekRange.start.toISOString().slice(0, 10);
    const endDate = props.weekRange.end.toISOString().slice(0, 10);

    const { data, error } = await getAbsencesForWeek(currentRestaurantId, startDate, endDate);

    if (error) {
      console.error('Failed to fetch absences:', error);
      absences.value = [];
    } else {
      absences.value = data?.absences || [];
    }
  } catch (error) {
    console.error('Error fetching absences:', error);
    absences.value = [];
  } finally {
    isLoadingAbsences.value = false;
  }
}

onMounted(() => {
  weekDays.value = getWeekDays(props.weekRange.start);
  loadData();
  fetchAbsences();
});

watch(
  () => props.weekRange,
  (r) => {
    weekDays.value = getWeekDays(r.start);
    loadData();
    fetchAbsences();
  },
  { deep: true },
);

function handleSlotClick({ day, slot }: { day: string; slot: ShiftSlot }) {
  const idx = selectedSlots.value.findIndex((s) => s.day === day && s.slot.id === slot.id);
  if (idx >= 0) {
    selectedSlots.value.splice(idx, 1);
  } else {
    selectedSlots.value.push({ day, slot });
  }
}

async function handleSlotRemove({ shiftAssignmentId }: { shiftAssignmentId: number }) {
  try {
    await fetchData(`${apiUrl}/planning/employees/${shiftAssignmentId}`, 'DELETE');
    await loadData(true);

    const currentSlots = [...selectedSlots.value];
    selectedSlots.value = [];
    await nextTick();
    selectedSlots.value = currentSlots;
  } catch (error) {
    console.error('Error removing shift assignment:', error);
  }
}

const showDuplicateModal = ref(false);

async function reloadAfterDuplicate() {
  await loadData(true);
}

const batchDialogVisible = ref(false);
function openBatchAssignDialog() {
  batchDialogVisible.value = true;
}

async function handleBatchSlotAdd() {
  // Just reload data after successful batch assignment from dialog
  await loadData(true);

  // Clear and reset selected slots to refresh UI
  const currentSlots = [...selectedSlots.value];
  selectedSlots.value = [];
  await nextTick();
  selectedSlots.value = currentSlots;
}

watch(batchDialogVisible, (visible) => {
  if (!visible) {
    selectedSlots.value = [];
  }
});

function handleSlotRowClick({ slot, days }: { slot: ShiftSlot; days: string[] }) {
  const allSelected = days.every((day) => selectedSlots.value.some((s) => s.day === day && s.slot.id === slot.id));
  if (allSelected) {
    selectedSlots.value = selectedSlots.value.filter((s) => s.slot.id !== slot.id);
  } else {
    const newSelections = days
      .filter((day) => !selectedSlots.value.some((s) => s.day === day && s.slot.id === slot.id))
      .map((day) => ({ day, slot }));
    selectedSlots.value = [...selectedSlots.value.filter((s) => s.slot.id !== slot.id), ...newSelections];
  }
}

// Computed for tab labels
const departmentLabels = computed(() => {
  const labels: Record<string, string> = {};
  for (const department of employeeStore.positionCategories) {
    labels[department] = t(`employee.kitchenPositions.departments.${department}`, department);
  }
  return labels;
});

// Computed for batch assign button label based on permissions
const batchAssignButtonLabel = computed(() => {
  if (can('employees_planning', 'create')) {
    return t('employee.planning.batchAssign.label');
  }
  return t('employee.planning.batchAssign.viewLabel');
});

async function handleTimeUpdate({
  shiftAssignmentId: _shiftAssignmentId,
  start_time: _start_time,
  end_time: _end_time,
}: {
  shiftAssignmentId: number;
  start_time?: string;
  end_time?: string;
}) {
  try {
    // Reload data to refresh the UI with updated times
    await loadData(true);

    // Maintain selected slots
    const currentSlots = [...selectedSlots.value];
    selectedSlots.value = [];
    await nextTick();
    selectedSlots.value = currentSlots;
  } catch (error) {
    console.error('Error handling time update:', error);
  }
}
</script>

<template>
  <main>
    <div v-if="isLoading" class="py-4 text-center">
      <i class="pi pi-spin pi-spinner" style="font-size: 2rem"></i>
    </div>
    <div v-else class="flex flex-col gap-4 px-4 py-4">
      <div v-if="can('employees_planning', 'create') && can('employees_planning', 'update')" class="my-4 text-right">
        <Button
          :disabled="!can('employees_planning', 'create') || !can('employees_planning', 'update')"
          :label="t('employee.planning.duplicateWeek.label')"
          icon="pi pi-copy"
          outlined
          @click="showDuplicateModal = true"
        />
      </div>

      <!-- Multiple Department Tabs -->
      <TabView v-if="employeeStore.positionCategories.length > 1" v-model:active-index="activeTabIndex">
        <TabPanel
          v-for="department in employeeStore.positionCategories"
          :key="department"
          :header="departmentLabels[department]"
          :pt="{
            header: { class: '!w-1/2' },
            headerAction: { class: '!justify-center' },
            content: { class: '!px-0' },
          }"
        >
          <div class="flex flex-col gap-4">
            <CalendarGrid
              :calendar-data="getCalendarDataForDepartment(department)"
              :days="weekDays.map((d) => d.toISOString().slice(0, 10))"
              :time-slots="filteredShiftSlots"
              :week-range="weekRange"
              :selected-slots="selectedSlots"
              @slot-click="handleSlotClick"
              @slot-row-click="handleSlotRowClick"
            />
            <div class="h-12 text-right">
              <Button v-show="selectedSlots.length > 0" :label="batchAssignButtonLabel" @click="openBatchAssignDialog" />
            </div>

            <!-- Absences Section -->
            <div v-if="absences.length > 0" class="mb-6">
              <p class="mb-3 font-semibold text-sm text-gray-800">{{ t('employee.planning.absences.title') }}</p>
              <div v-if="isLoadingAbsences" class="text-center text-gray-600">
                <i class="pi pi-spin pi-spinner"></i>
                {{ t('common.loading') }}
              </div>
              <div v-else class="space-y-2">
                <div v-for="absence in absences" :key="absence.employee_name" class="flex flex-wrap items-center text-gray-700">
                  <span class="text-sm font-medium">{{ absence.employee_name }} : </span>
                  <span class="ml-1 text-sm">{{ absence.date_ranges.join(', ') }}</span>
                  <span v-if="absence.total_days > 1" class="ml-1 text-sm text-gray-500">
                    ({{ t('dayOff.groupedDays', { count: absence.total_days }) }})
                  </span>
                </div>
              </div>
            </div>

            <KitchenPositionsWithUsers
              :positions="employeeStore.usersByKitchenPositionAndCategory[department] || []"
              :title="t('employee.kitchenPositions.label')"
              :no-users-text="t('employee.slots.noUsers')"
            />
          </div>
        </TabPanel>
      </TabView>

      <!-- Single Department (fallback) -->
      <div v-else class="flex flex-col gap-4">
        <CalendarGrid
          :calendar-data="calendarGridData"
          :days="weekDays.map((d) => d.toISOString().slice(0, 10))"
          :time-slots="filteredShiftSlots"
          :week-range="weekRange"
          :selected-slots="selectedSlots"
          @slot-click="handleSlotClick"
          @slot-row-click="handleSlotRowClick"
        />
        <div class="h-12 text-right">
          <Button v-show="selectedSlots.length > 0" :label="batchAssignButtonLabel" @click="openBatchAssignDialog" />
        </div>

        <!-- Absences Section -->
        <div v-if="absences.length > 0" class="mb-6 rounded-lg bg-gray-50 p-4">
          <h3 class="mb-3 font-semibold text-lg text-gray-800">{{ t('employee.planning.absences.title') }}</h3>
          <div v-if="isLoadingAbsences" class="text-center text-gray-600">
            <i class="pi pi-spin pi-spinner"></i>
            {{ t('common.loading') }}
          </div>
          <div v-else class="space-y-2">
            <div v-for="absence in absences" :key="absence.employee_name" class="flex items-center text-gray-700">
              <span class="font-medium">{{ absence.employee_name }}:</span>
              <span class="ml-2">{{ absence.date_ranges.join(', ') }}</span>
              <span v-if="absence.total_days > 1" class="ml-1 text-sm text-gray-500">
                ({{ t('employee.planning.absences.totalDays', { count: absence.total_days }) }})
              </span>
            </div>
          </div>
        </div>

        <!-- Show all departments combined for single view -->
        <div v-for="(positions, department) in employeeStore.usersByKitchenPositionAndCategory" :key="department" class="mb-6">
          <KitchenPositionsWithUsers
            :positions="positions"
            :title="`${t('employee.kitchenPositions.label')} - ${departmentLabels[department]}`"
            :no-users-text="t('employee.slots.noUsers')"
          />
        </div>
      </div>
    </div>

    <DuplicateWeekShiftDialog
      :visible="showDuplicateModal"
      :week-range="props.weekRange"
      :restaurant-id="currentRestaurantId"
      @update:visible="showDuplicateModal = $event"
      @duplicated="reloadAfterDuplicate"
    />
    <SlotDetailsDialog
      v-if="batchDialogVisible"
      :visible="batchDialogVisible"
      :selected-slots="selectedSlots"
      :employees="employeeStore.restaurantUsers"
      :kitchen-positions="employeeStore.kitchenPositions"
      :kitchen-positions-by-category="employeeStore.kitchenPositionsByCategory"
      :current-category="currentDepartment"
      :shifts-by-day-and-slot="employeeStore.shiftsByDayAndSlot"
      @update:visible="batchDialogVisible = $event"
      @add="handleBatchSlotAdd"
      @remove="handleSlotRemove"
      @update-time="handleTimeUpdate"
    />
  </main>
</template>

<style scoped>
select,
button {
  padding: 0.25rem 0.5rem;
}
</style>
